import { useLocation } from "wouter";
import { useAuth } from "@/_core/hooks/useAuth";
import { useActiveWorkspace } from "@/contexts/ActiveWorkspace";
import { useGlobalDialogs } from "@/contexts/GlobalDialogs";
import { useI18n } from "@/lib/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { NudgeInbox } from "@/components/NudgeInbox";
// Retayf brand lockups — the approved D3 symbol + W1 wordmark, taken verbatim
// from the final-mark package. They are OUTLINED PATHS with no live text and no
// linked font, so they render identically everywhere and are immune to the
// `.font-display { letter-spacing: -0.02em }` rule in index.css that would
// otherwise distort an Arabic wordmark. Ink is the primary; the paper cut is the
// reversal for dark mode. Exactly one of the pair is ever displayed, so exactly
// one ever reaches the accessibility tree.
import lockupEnInk from "@/assets/brand/retayf-lockup-en-ink.svg";
import lockupEnPaper from "@/assets/brand/retayf-lockup-en-paper.svg";
import lockupArInk from "@/assets/brand/retayf-lockup-ar-ink.svg";
import lockupArPaper from "@/assets/brand/retayf-lockup-ar-paper.svg";
import {
  ChevronDown,
  Flame,
  Lock,
  LogOut,
  Plug,
  Settings as SettingsIcon,
  User as UserIcon,
  Users,
} from "lucide-react";

/**
 * Reusable app chrome (Retayf lockup + workspace picker + bell + avatar +
 * optional streak). Passed to `TabScaffold`'s generic `topBar` slot — it holds
 * no tab-specific logic and computes nothing sensitive.
 */
function initials(name?: string | null) {
  return (name || "U")
    .split(" ")
    .slice(0, 2)
    .map(p => p[0])
    .join("")
    .toUpperCase();
}

export function AppTopBar({ weeklyStreak }: { weeklyStreak?: number }) {
  const { user, logout } = useAuth();
  const { workspaces, active, setActiveId } = useActiveWorkspace();
  const [, navigate] = useLocation();
  // Settings/Integrations dialogs are hosted by GlobalDialogsProvider at the
  // app root, so the shortcuts work from every page that renders this bar.
  const dialogs = useGlobalDialogs();
  const { locale } = useI18n();
  const isAr = locale === "ar";

  return (
    <div className="mx-auto w-full max-w-lg px-4 pt-3 pb-1 space-y-2.5">
      {/* Row 1 — brand + notifications + account */}
      <div className="flex items-center gap-3">
        {/*
          The brand lockup. This replaced a coral tile containing the literal
          character "R" plus a hardcoded "Receipt" and a persistent tagline.
          The tagline is gone from application chrome by decision — it belongs
          to the sign-in screen, the browser title and marketing, not to every
          screen. Row height is unaffected: it is pinned by the h-9 avatar and
          the bell, so removing the second line moves nothing.

          The mark is NOT mirrored in Arabic — only the lockup ORDER differs,
          which is baked into the supplied Arabic master. Do not add
          `font-display` here: these are paths, not text.
        */}
        <div className="flex items-center min-w-0">
          <img
            src={isAr ? lockupArInk : lockupEnInk}
            alt={isAr ? "ريتايف" : "Retayf"}
            data-testid="app-brand-lockup"
            className="h-7 w-auto shrink-0 dark:hidden"
            draggable={false}
          />
          <img
            src={isAr ? lockupArPaper : lockupEnPaper}
            alt={isAr ? "ريتايف" : "Retayf"}
            data-testid="app-brand-lockup-dark"
            className="h-7 w-auto shrink-0 hidden dark:block"
            draggable={false}
          />
        </div>

        <div className="ms-auto flex items-center gap-2">
          {/*
            The 🔔 inbox — the SAME component `AppHeader` mounts, not a copy.
            Until this fix a placeholder <button><Bell/></button> sat here with
            NO onClick, so tapping the bell did nothing on every tabbed route.
            `NudgeInbox` was reachable only through `AppHeader`, whose only
            importer is the pilot-locked DepartmentReport — so the in-app inbox
            has been unreachable since the tabbed shell shipped.

            It takes no props and owns its own open state, i18n and queries
            (`insights.list` / `insights.unreadCount`), which the server governs
            through INBOX_VISIBLE_TYPES. The client does no type filtering of
            its own and must not start: retired types are excluded server side,
            by policy, not here.
          */}
          <NudgeInbox />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button aria-label={isAr ? "الحساب" : "Account"} className="rounded-full">
                <Avatar className="h-9 w-9 border border-border/70 shadow-sm">
                  <AvatarFallback className="text-xs font-semibold bg-sand text-ink">
                    {initials(user?.name)}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel className="truncate">{user?.name ?? "—"}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {/* The agreed Web-v1 avatar menu: profile + settings +
                  integrations + sign out. Admin destinations live in
                  More → Admin only — never in this menu. */}
              <DropdownMenuItem onClick={() => navigate("/me")} className="cursor-pointer">
                <UserIcon className="me-2 h-4 w-4" /> {isAr ? "ملفي الشخصي" : "My profile"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => dialogs.openSettings()} className="cursor-pointer">
                <SettingsIcon className="me-2 h-4 w-4" /> {isAr ? "الإعدادات" : "Settings"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => dialogs.openIntegrations()} className="cursor-pointer">
                <Plug className="me-2 h-4 w-4" /> {isAr ? "التكاملات" : "Integrations"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => logout()} className="cursor-pointer text-destructive focus:text-destructive">
                <LogOut className="me-2 h-4 w-4" /> {isAr ? "تسجيل الخروج" : "Sign out"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Row 2 — workspace picker + streak */}
      <div className="flex items-center gap-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card px-3 py-1.5 text-xs font-medium max-w-[60vw] shadow-sm">
              {active?.team.isPersonal ? (
                <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              ) : (
                <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              )}
              <span className="truncate">{active?.team.name ?? (isAr ? "مساحة العمل" : "Workspace")}</span>
              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel>{isAr ? "تبديل مساحة العمل" : "Switch workspace"}</DropdownMenuLabel>
            {workspaces.map(w => (
              <DropdownMenuItem
                key={w.team.id}
                onClick={() => setActiveId(w.team.id)}
                className="cursor-pointer"
              >
                {w.team.isPersonal ? (
                  <Lock className="me-2 h-4 w-4" />
                ) : (
                  <Users className="me-2 h-4 w-4" />
                )}
                <span className="truncate">{w.team.name}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {typeof weeklyStreak === "number" && weeklyStreak > 0 ? (
          <span className="ms-auto inline-flex items-center gap-1.5 rounded-full bg-coral-soft/60 text-coral px-3 py-1 text-xs font-semibold shrink-0">
            <Flame className="h-3.5 w-3.5" /> {isAr ? `سلسلة ${weeklyStreak} أسبوع` : `${weeklyStreak}-week streak`}
          </span>
        ) : null}
      </div>
    </div>
  );
}
